import type { SceneManifestV2 } from '@lightscape/manifest'

export type ManifestEnvelope = {
  version: number
  payload: SceneManifestV2 | Record<string, unknown>
}

export type ProjectSummary = {
  id: string
  title: string
  slug?: string | null
  draftUpdatedAt: string
  thumbnailUrl?: string
  description?: string
  width?: number
  height?: number
  layerCount?: number
  effectCount?: number
  /** True while the project's public link is live. */
  isPublished?: boolean
}

export type ProjectRecord = ProjectSummary & {
  draftManifest: ManifestEnvelope
  createdAt: string
  updatedAt: string
}

export type VersionRecord = {
  id: string
  versionNo: number
  message?: string
  createdAt: string
  publishedAt?: string
  pubSlug?: string
}

export type AssetRecord = {
  id: string
  hash: string
  url: string
  mime: string
  byteSize: number
  width?: number
  height?: number
}

export type Publication = {
  id?: string
  pubSlug: string
  manifestUrl: string
  thumbnailUrl: string
  publishedAt: string
  versionId?: string
  versionNo?: number
  /** Stable public slug of the project (kept across republishes). */
  slug?: string
  /** Public share link (`/s/:slug`, with link previews) and iframe embed URL. */
  shareUrl?: string
  embedUrl?: string
}

export type ListOptions = {
  page?: number
  pageSize?: number
  q?: string
}

export type ListResult<T> = {
  items: T[]
  total: number
  page: number
  pageSize: number
}

export class StorageAdapterError extends Error {
  readonly code: string
  readonly status?: number
  constructor(code: string, message: string, status?: number) {
    super(message)
    this.name = 'StorageAdapterError'
    this.code = code
    this.status = status
  }
}

export class ProjectNotFoundError extends StorageAdapterError {
  constructor(id: string) {
    super('PROJECT_NOT_FOUND', `Project ${id} not found`, 404)
  }
}

export class BadOwnerTokenError extends StorageAdapterError {
  constructor() {
    super('BAD_OWNER_TOKEN', 'Owner token required or invalid', 401)
  }
}

export class AssetTooLargeError extends StorageAdapterError {
  constructor(bytes: number) {
    super('ASSET_TOO_LARGE', `Asset ${bytes} bytes exceeds limit`, 413)
  }
}
